import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import { X, ShieldCheck, CheckCircle2, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BypassRegistrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    conferences: any[];
}

export default function BypassRegistrationModal({ isOpen, onClose, conferences }: BypassRegistrationModalProps) {
    const queryClient = useQueryClient();

    // Form State
    const [conferenceId, setConferenceId] = useState("");
    const [isPresenter, setIsPresenter] = useState("0");
    const [participantType, setParticipantType] = useState("general");
    const [currency, setCurrency] = useState("IDR");
    const [tierName, setTierName] = useState("regular");
    const [isInternalStudent, setIsInternalStudent] = useState("0");

    const [participantName, setParticipantName] = useState("");
    const [email, setEmail] = useState("");
    const [whatsappNumber, setWhatsappNumber] = useState("");
    const [institutionName, setInstitutionName] = useState("");

    const [ojsSubmissionId, setOjsSubmissionId] = useState("");
    const [submissionTitle, setSubmissionTitle] = useState("");
    const [submissionAuthors, setSubmissionAuthors] = useState("");
    const [presenterNames, setPresenterNames] = useState("");

    const [nim, setNim] = useState("");
    const [nik, setNik] = useState("");
    const [ktmFile, setKtmFile] = useState<File | null>(null);

    // Flow State
    const [priceData, setPriceData] = useState<any>(null);

    const resetForm = () => {
        setConferenceId("");
        setIsPresenter("0");
        setParticipantType("general");
        setCurrency("IDR");
        setTierName("regular");
        setIsInternalStudent("0");
        setParticipantName("");
        setEmail("");
        setWhatsappNumber("");
        setInstitutionName("");
        setOjsSubmissionId("");
        setSubmissionTitle("");
        setSubmissionAuthors("");
        setPresenterNames("");
        setNim("");
        setNik("");
        setKtmFile(null);
        setPriceData(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    // 1. Cek Harga Bypass
    const checkPriceMutation = useMutation({
        mutationFn: async () => {
            const payload = {
                is_presenter: isPresenter === "1",
                participant_type: participantType,
                currency: currency,
                tier_name: tierName,
                quantity: 1
            };
            const response = await axiosClient.post("/conference-billing/check-price-bypass", payload);
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message);
                setPriceData(null);
            } else {
                toast.success(data.message);
                setPriceData(data.data);
            }
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message || "Gagal mengecek harga.";
            toast.error(msg);
            setPriceData(null);
        }
    });

    // 1.5 Cek Naskah OJS
    const checkSubmissionMutation = useMutation({
        mutationFn: async () => {
            if (!conferenceId) throw new Error("Silakan pilih Conference terlebih dahulu.");
            if (!ojsSubmissionId) throw new Error("Silakan isi ID Submission OJS.");
            
            const selectedConference = conferences.find((c: any) => (c.conference_id || c.id)?.toString() === conferenceId);
            if (!selectedConference) throw new Error("Data conference tidak valid.");

            const payload = {
                slug: selectedConference.slug,
                ojs_submission_id: parseInt(ojsSubmissionId)
            };
            const response = await axiosClient.post("/conference-billing/check-submission", payload);
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message);
            } else {
                toast.success("Data Naskah OJS ditemukan!");
                setSubmissionTitle(data.data.title);
                setSubmissionAuthors(data.data.authors);
            }
        },
        onError: (error: any) => {
            let msg = "Terjadi kesalahan.";
            if (error.response?.data?.errors) {
                msg = error.response.data.errors[Object.keys(error.response.data.errors)[0]][0];
            } else if (error.response?.data?.message) {
                msg = error.response.data.message;
            } else if (error instanceof Error) {
                msg = error.message;
            }
            toast.error(msg);
        }
    });

    // 2. Submit Bypass
    const submitBypassMutation = useMutation({
        mutationFn: async () => {
            if (!conferenceId) throw new Error("Silakan pilih Conference.");
            if (!participantName || !email || !whatsappNumber || !institutionName) throw new Error("Data diri peserta belum lengkap.");

            if (isPresenter === "1" && (!ojsSubmissionId || !submissionTitle || !presenterNames)) {
                throw new Error("Data Naskah OJS belum lengkap.");
            }

            const formData = new FormData();
            formData.append("conference_id", conferenceId);
            formData.append("is_presenter", isPresenter);
            formData.append("participant_name", participantName);
            formData.append("email", email);
            formData.append("whatsapp_number", whatsappNumber);
            formData.append("institution_name", institutionName);
            formData.append("currency", currency);
            formData.append("participant_type", participantType);
            formData.append("is_internal_student", isInternalStudent);
            formData.append("tier_name", tierName);
            formData.append("quantity", "1");
            formData.append("gateway_transaction_id", currency === "USD" ? "9" : "8");

            if (isPresenter === "1") {
                formData.append("ojs_submission_id", ojsSubmissionId);
                formData.append("submission_title", submissionTitle);
                formData.append("submission_authors", submissionAuthors);
                formData.append("presenter_names", presenterNames);
            }

            if (participantType !== "general") {
                if (isInternalStudent === "1") {
                    formData.append("nim", nim);
                    formData.append("nik", nik);
                } else if (ktmFile) {
                    formData.append("ktm_file", ktmFile);
                }
            }

            const response = await axiosClient.post("/conference-billing/store-bypass", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message);
            } else {
                toast.success(data.message || "Bypass pendaftaran berhasil!");
                queryClient.invalidateQueries({ queryKey: ["conference-verifications"] });
                handleClose();
            }
        },
        onError: (error: any) => {
            const msg = error instanceof Error
                ? error.message
                : (error.response?.data?.message || error.response?.data?.errors?.[Object.keys(error.response?.data?.errors)[0]][0] || "Terjadi kesalahan.");
            toast.error(msg);
        }
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                            <ShieldCheck className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-slate-900 dark:text-white">Bypass Pendaftaran</h2>
                            <p className="text-sm text-slate-500">Buat tagihan khusus untuk paket yang sudah lewat tenggat.</p>
                        </div>
                    </div>
                    <button onClick={handleClose} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* Tahap 1: Pengaturan Paket */}
                    <div className="space-y-4">
                        <h3 className="font-bold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2">1. Konfigurasi Paket Bypass</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Conference</label>
                                <select value={conferenceId} onChange={(e) => setConferenceId(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                                    <option value="">-- Pilih Conference --</option>
                                    {conferences?.map((c: any) => (
                                        <option key={c.conference_id || c.id} value={c.conference_id || c.id}>{c.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Kategori Peserta</label>
                                <select value={participantType} onChange={(e) => { setParticipantType(e.target.value); setPriceData(null); }} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                                    <option value="general">Umum (General)</option>
                                    <option value="student_s1_s2">Mahasiswa (S1/S2/D4)</option>
                                    <option value="student_s3">Mahasiswa (S3)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Role</label>
                                <select value={isPresenter} onChange={(e) => { setIsPresenter(e.target.value); setPriceData(null); }} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                                    <option value="0">Non-Presenter</option>
                                    <option value="1">Presenter</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Mata Uang & Kewarganegaraan</label>
                                <select value={currency} onChange={(e) => { setCurrency(e.target.value); setPriceData(null); }} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                                    <option value="IDR">IDR (WNI)</option>
                                    <option value="USD">USD (WNA)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Tier Paket (Bypass)</label>
                                <select value={tierName} onChange={(e) => { setTierName(e.target.value); setPriceData(null); }} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                                    <option value="early_bird">Early Bird</option>
                                    <option value="regular">Regular</option>
                                </select>
                            </div>
                        </div>

                        <Button
                            onClick={() => checkPriceMutation.mutate()}
                            disabled={checkPriceMutation.isPending}
                            className="w-full sm:w-auto mt-4 bg-slate-800 hover:bg-slate-900 text-white rounded-xl"
                        >
                            {checkPriceMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                            Cek Ketersediaan & Harga
                        </Button>

                        {priceData && (
                            <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-emerald-800 font-semibold">{priceData.package_name}</p>
                                    <p className="text-xs text-emerald-600 mt-0.5">Tier: {priceData.tier_name.toUpperCase()}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-emerald-600">Total Harga</p>
                                    <p className="text-lg font-black text-emerald-700">{priceData.currency} {new Intl.NumberFormat(priceData.currency === 'IDR' ? 'id-ID' : 'en-US').format(priceData.total_amount)}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Tahap 2: Detail Peserta */}
                    {priceData && (
                        <>
                            <div className="space-y-4">
                                <h3 className="font-bold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2">2. Data Peserta</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Nama Lengkap</label>
                                        <input type="text" value={participantName} onChange={(e) => setParticipantName(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="John Doe" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Email</label>
                                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">No. WhatsApp</label>
                                        <input type="text" value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="08123456789" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Institusi</label>
                                        <input type="text" value={institutionName} onChange={(e) => setInstitutionName(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="Universitas XYZ" />
                                    </div>
                                </div>
                            </div>

                            {/* Data Presenter Khusus */}
                            {isPresenter === "1" && (
                                <div className="space-y-4">
                                    <h3 className="font-bold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2">Data Naskah OJS</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">ID Submission OJS</label>
                                            <div className="flex gap-2">
                                                <input type="text" value={ojsSubmissionId} onChange={(e) => setOjsSubmissionId(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="Contoh: 1234" />
                                                <Button type="button" onClick={() => checkSubmissionMutation.mutate()} disabled={checkSubmissionMutation.isPending || !ojsSubmissionId} className="bg-slate-800 hover:bg-slate-900 text-white rounded-xl px-4 shrink-0 shadow-sm">
                                                    {checkSubmissionMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Cek OJS
                                                </Button>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Judul Naskah</label>
                                            <input type="text" value={submissionTitle} onChange={(e) => setSubmissionTitle(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="Judul artikel..." />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Semua Author (Berdasarkan OJS)</label>
                                            <input type="text" value={submissionAuthors} onChange={(e) => setSubmissionAuthors(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="John, Jane, Doe" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Nama Presenter Saja</label>
                                            <input type="text" value={presenterNames} onChange={(e) => setPresenterNames(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" placeholder="John" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Data Mahasiswa Khusus */}
                            {participantType !== "general" && (
                                <div className="space-y-4">
                                    <h3 className="font-bold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2">Verifikasi Mahasiswa</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Apakah Mahasiswa Undiksha?</label>
                                            <select value={isInternalStudent} onChange={(e) => setIsInternalStudent(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none">
                                                <option value="0">Bukan (Eksternal)</option>
                                                <option value="1">Ya (Mahasiswa Internal Undiksha)</option>
                                            </select>
                                        </div>

                                        {isInternalStudent === "1" ? (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">NIM</label>
                                                    <input type="text" value={nim} onChange={(e) => setNim(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">NIK</label>
                                                    <input type="text" value={nik} onChange={(e) => setNik(e.target.value)} className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500" />
                                                </div>
                                            </>
                                        ) : (
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">File KTM (PDF/Image)</label>
                                                <input type="file" accept=".pdf,image/*" onChange={(e) => setKtmFile(e.target.files ? e.target.files[0] : null)} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-3 shrink-0">
                    <Button variant="ghost" onClick={handleClose} disabled={submitBypassMutation.isPending} className="rounded-xl">Batal</Button>
                    <Button
                        onClick={() => submitBypassMutation.mutate()}
                        disabled={!priceData || submitBypassMutation.isPending}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                    >
                        {submitBypassMutation.isPending ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Memproses...</>
                        ) : (
                            <><CheckCircle2 className="w-4 h-4 mr-2" /> Buat Tagihan Bypass</>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}




